import {createContext, useContext, useEffect, useReducer} from 'react';
import FinishSound from '../sounds/finished.flac';
import {db} from "../db";
import { isFeatureEnabled } from '../config/featureFlags';

// Lazy import to avoid circular dependency
let jobManager = null;
const getJobManager = () => {
    if (!jobManager && typeof window !== 'undefined') {
        jobManager = require('../services/JobManager').default;
    }
    return jobManager;
};

export const GenerateContext = createContext(null);
export const GenerateDispatchContext = createContext(null);

export function GenerateProvider({children}) {
    const [generateStatus, dispatch] = useReducer(generateReducer, {
        // Backward compatibility fields
        uid: '',
        loading: false,
        status: 'ready',
        progress: 0,
        message: "",
        tag: "",
        intervalId: null,
        serverStatus: {},
        abortControllers: [],
        count: 0,
        totalImages: 0,
        failedImage: false,
        failedImageID: null,
        failedWorkloadUID: null,
        undeliveredImages : false,
        checkingUndeliveredImages: false,
        retrievingUndeliveredImages: false,
        totalUndeliveredImages: 0,
        totalRetrievedImages: 0,
        cancellingUndeliveredCheck: false,
        // New multi-job state
        jobs: {} // { [uid]: { uid, status, progress, count, totalImages, error, outputs: [] } }
    });
    useEffect(() => {
        return () => clearInterval(generateStatus.intervalId);
    }, []);

    return (
        <GenerateContext.Provider value={generateStatus}>
            <GenerateDispatchContext.Provider value={dispatch}>
                {children}
            </GenerateDispatchContext.Provider>
        </GenerateContext.Provider>
    );
}

function abortAllControllers(abortControllers) {
    if (abortControllers.length > 0) {
        abortControllers.forEach(controller => controller.abort());
    }
}

function generateReducer(generateStatus, action) {
    switch(action.type) {
        // Multi-job support
        case 'addJob': {
            const newJobState = {
                uid: action.uid,
                status: 'generating',
                progress: 0,
                count: action.count,
                totalImages: 0,
                error: null,
                outputs: [],
                processedUIDs: [], // Track which output UIDs have been processed
                timestamp: Date.now(),
                lastProgressTime: Date.now(), // Track when progress last changed
                lastProgress: 0, // Track last progress value to detect changes
                serverUrl: action.serverUrl, // Store server URL for progress polling
                modelName: action.modelName, // Store model name for reference
                serverType: action.serverType // Store server type (gpu/cpu/cosy)
            };

            // Sync with job manager if enabled (but not if we're already inside job manager)
            if (isFeatureEnabled('USE_NEW_JOB_MANAGER') && !action.fromJobManager) {
                const manager = getJobManager();
                if (manager) {
                    manager.addJob(action.uid, { count: action.count });
                }
            }

            return {
                ...generateStatus,
                jobs: {
                    ...generateStatus.jobs,
                    [action.uid]: newJobState
                }
            };
        }
        case 'updateJob': {
            const existingJob = generateStatus.jobs[action.uid];
            if (!existingJob) {
                return generateStatus; // Don't recreate a ghost job
            }
            if (action.setting) {
                try {
                    const successAudio = new Audio(FinishSound);
                    successAudio.loop = false;
                    successAudio.volume = 0.4;
                    successAudio.play().catch(e => {
                        // Ignore audio play errors (browser autoplay policies)
                        console.log('Could not play sound:', e.message);
                    });
                } catch (error) {
                    console.log('Error creating audio:', error);
                }
            }
            return {
                ...generateStatus,
                jobs: {
                    ...generateStatus.jobs,
                    [action.uid]: { ...existingJob, ...action.updates }
                }
            };
        }
        case 'incrementJobImages': {
            const existingJob = generateStatus.jobs[action.uid];
            if (!existingJob) {
                return generateStatus;
            }
            
            // Atomic increment - doesn't depend on reading the value first
            // const newTotalImages = (existingJob.totalImages || 0) + (action.count || 1);
            // console.log(`[incrementJobImages] New total: ${newTotalImages}`);
            
            // Play sound if requested
            if (action.setting) {
                try {
                    const successAudio = new Audio(FinishSound);
                    successAudio.loop = false;
                    successAudio.volume = 0.4;
                    successAudio.play().catch(e => {
                        // Ignore audio play errors (browser autoplay policies)
                        console.log('Could not play sound:', e.message);
                    });
                } catch (error) {
                    console.log('Error creating audio:', error);
                }
            }
            
            return {
                ...generateStatus,
                jobs: {
                    ...generateStatus.jobs,
                    [action.uid]: { ...existingJob, totalImages: existingJob.totalImages + 1}
                }
            };
        }
        case 'addProcessedUID': {
            const existingJob = generateStatus.jobs[action.jobUid];
            if (!existingJob) {
                return generateStatus;
            }

            // Add the output UID to the processed list
            const updatedProcessedUIDs = [...(existingJob.processedUIDs || []), action.outputUid];
            
            return {
                ...generateStatus,
                jobs: {
                    ...generateStatus.jobs,
                    [action.jobUid]: { ...existingJob, processedUIDs: updatedProcessedUIDs }
                }
            };
        }
        case 'cancelJob': {
            const existingJob = generateStatus.jobs[action.uid];
            if (!existingJob) {
                return generateStatus;
            }
            return {
                ...generateStatus,
                jobs: {
                    ...generateStatus.jobs,
                    [action.uid]: {
                        ...existingJob,
                        status: 'cancelling'
                    }
                }
            };
        }
        case 'removeJob': {
            // Sync with job manager if enabled (but not if we're already inside job manager)
            if (isFeatureEnabled('USE_NEW_JOB_MANAGER') && !action.fromJobManager) {
                const manager = getJobManager();
                if (manager) {
                    manager.removeJob(action.uid);
                }
            }

            const newJobs = { ...generateStatus.jobs };
            delete newJobs[action.uid];
            return { ...generateStatus, jobs: newJobs };
        }
        // Existing single-job flow remains unchanged
        case 'loading': {
            return {...generateStatus, loading: true, status: 'loading', message: "", intervalId: action.intervalId};
        }
        case 'generate': {
            localStorage.setItem('remember_workload', JSON.stringify({
                active: true,
                uid: action.uid,
                count: action.count,
                totalImage: 0,
                num: 0,
            }))
            return {...generateStatus, uid: action.uid, status: 'generating', message: "", count: action.count, totalImages: 0};
        }
        case 'cancel': {
            abortAllControllers(generateStatus.abortControllers);
            clearInterval(generateStatus.intervalId);
            return {...generateStatus, loading: false, status: 'canceling', progress: 0, intervalId: null, serverStatus: generateStatus.serverStatus, abortControllers: [], failedImage: false, failedImageID: null, failedWorkloadUID: null, undeliveredImages: false, checkingUndeliveredImages: false, retrievingUndeliveredImages: false};
        }
        case 'addAbortController': {
            return {...generateStatus, abortControllers: [...generateStatus.abortControllers, action.abortController]};
        }
        case 'progress': {
            return {...generateStatus, progress: action.progress, message: ""};
        }
        case 'imageProgress': {
            // console.log("Image Progress: ", action.setting);
            if (action.setting) {
                try {
                    const successAudio = new Audio(FinishSound);
                    successAudio.loop = false;
                    successAudio.volume = 0.4;
                    successAudio.play().catch(e => {
                        // Ignore audio play errors (browser autoplay policies)
                        console.log('Could not play sound:', e.message);
                    });
                } catch (error) {
                    console.log('Error creating audio:', error);
                }
            }

            return {...generateStatus, totalImages: generateStatus.totalImages + 1};
        }
        case 'setModels': {
            return {...generateStatus};
        }
        case 'setServerStatus': {
            return {...generateStatus, serverStatus: action.serverStatus};
        }
        case 'clearError': {
            return {...generateStatus, message: ""};
        }
        case 'ready': {
            abortAllControllers(generateStatus.abortControllers);

            const workloadItem = localStorage.getItem("remember_workload");
            if (workloadItem) {
                try {
                    const workloadDetails = JSON.parse(workloadItem);
                    if (workloadDetails && workloadDetails.active) {
                        localStorage.setItem('remember_workload', JSON.stringify({
                            active: false,
                            uid: "",
                            count: 0,
                            totalImage: 0,
                            num: 0,
                        }))
                    }
                } catch (e) {
                    console.error('Error parsing remember_workload:', e);
                }
            }

            if (generateStatus.intervalId) {
                clearInterval(generateStatus.intervalId);
            }
            return {...generateStatus, uid: '', loading: false, status: 'ready', progress: 0, message: "", count: 0, totalImages: 0, intervalId: null, serverStatus: generateStatus.serverStatus, abortControllers: [], failedImage: false, failedImageID: null, failedWorkloadUID: null, undeliveredImages: false, checkingUndeliveredImages: false, retrievingUndeliveredImages: false, jobs: generateStatus.jobs || {}};
        }
        case 'done': {
            abortAllControllers(generateStatus.abortControllers);

            const workloadItem = localStorage.getItem("remember_workload");
            if (workloadItem) {
                try {
                    const workloadDetails = JSON.parse(workloadItem);
                    if (workloadDetails && workloadDetails.active) {
                        localStorage.setItem('remember_workload', JSON.stringify({
                            active: false,
                            uid: "",
                            count: 0,
                            totalImage: 0,
                            num: 0,
                        }))
                    }
                } catch (e) {
                    console.error('Error parsing remember_workload:', e);
                }
            }

            if (generateStatus.intervalId) {
                clearInterval(generateStatus.intervalId);
            }

            return {...generateStatus, uid: '', loading: false, status: 'done', progress: 0, message: "", count: 0, totalImages: 0, intervalId: null, serverStatus: generateStatus.serverStatus, abortControllers: [], failedImage: false, failedImageID: null, failedWorkloadUID: null, undeliveredImages: false, checkingUndeliveredImages: false, retrievingUndeliveredImages: false, jobs: generateStatus.jobs || {}};
        }
        case 'tagging': {
            return {...generateStatus, tag: action.tag}
        }
        case 'error': {
            abortAllControllers(generateStatus.abortControllers);
            if (generateStatus.intervalId) {
                clearInterval(generateStatus.intervalId);
            }
            return {...generateStatus, uid: '', loading: false, status: 'error', progress: 0, message: action.message, count: 0, totalImages: 0, intervalId: null, serverStatus: generateStatus.serverStatus, abortControllers: [], failedImage: false, failedImageID: null, failedWorkloadUID: null, undeliveredImages: false, checkingUndeliveredImages: false, retrievingUndeliveredImages: false, jobs: generateStatus.jobs || {}};
        }
        case 'failedImage': {
            return {...generateStatus, failedImage: true};
        }
        case 'failedImageID': {
            return {...generateStatus, failedImageID: action.failedImageID};
        }
        case 'failedWorkloadUID': {
            return {...generateStatus, failedWorkloadUID: action.failedWorkloadUID};
        }
        case 'undeliveredImages': {
            return {...generateStatus, undeliveredImages: true, checkingUndeliveredImages: false, retrievingUndeliveredImages: false};
        }
        case 'checkingUndeliveredImages': {
            return {...generateStatus, checkingUndeliveredImages: true, retrievingUndeliveredImages: false};
        }
        case 'retrievingUndeliveredImages': {
            return {...generateStatus, retrievingUndeliveredImages: true, checkingUndeliveredImages: false};
        }
        case 'totalUndeliveredImages': {
            return {...generateStatus, totalUndeliveredImages: action.totalUndeliveredImages, totalRetrievedImages: 0};
        }
        case 'totalRetrievedImages': {
            return {...generateStatus, totalRetrievedImages: generateStatus.totalRetrievedImages + 1};
        }
        case 'cancellingUndeliveredCheck': {
            return {...generateStatus, cancellingUndeliveredCheck: true};
        }
        case 'resetcancellingUndeliveredCheck': {
            return {...generateStatus, cancellingUndeliveredCheck: false};
        }
        default: return generateStatus;
    }
}

export function useGenerateStatus() { return useContext(GenerateContext); }
export function useGenerateDispatch() { return useContext(GenerateDispatchContext); }
